/**
 * ActivationChargeConfirmModal — Shows charge confirmation before activating.
 * 
 * Props:
 *   isOpen: boolean
 *   onConfirm: () => void — called when user confirms the charge
 *   onCancel: () => void — called when user cancels
 *   last4: string — last 4 digits of card
 *   brand: string — card brand (visa, mastercard, etc.)
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from '@/components/ui/button';
import { CreditCard, ShieldCheck } from 'lucide-react';

const ActivationChargeConfirmModal = ({ isOpen, onConfirm, onCancel, last4, brand }) => {
  if (!isOpen) return null;

  const brandDisplay = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onCancel?.(); }}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-sm" data-testid="activation-charge-confirm-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
            Activate Manuscript
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-center pt-2">
            Activating this manuscript will charge <span className="text-white font-semibold">$9.99</span> to
            your {brandDisplay} card ending in <span className="text-white font-semibold">{last4}</span>.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-3 sm:justify-center pt-4">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-slate-300 flex-1"
            data-testid="activation-charge-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1"
            data-testid="activation-charge-confirm-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Confirm & Activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ActivationChargeConfirmModal;
